import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ReceiptText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader } from "@/components/common/ui-bits";
import { BillFormDialog, BillSummaryRow } from "@/components/bills/BillParts";
import { BillDetailDialog } from "@/components/bills/BillDetailDialog";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkBar, BulkDeleteDialog } from "@/components/common/BulkBar";
import { ScanButton } from "@/components/scan/Scan";

const EMPTY = { status: "all", category_id: "all", vendor: "", due_from: "", due_to: "", period_from: "", period_to: "" };

export default function BillsPage() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const onScan = (r) => {
    const f = r.fields;
    const p = { vendor: f.vendor, bill_number: f.bill_number, account_number: f.account_number, amount: f.amount, period_start: f.period_start,
      period_end: f.period_end, due_date: f.due_date, category_id: f.category_id || "", photo_id: r.photo_id };
    setPrefill({ form: p, ai: new Set(Object.keys(p).filter((k) => p[k] && k !== "photo_id")) });
    setAdding(true);
  };
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const openId = params.get("bill");
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/bills/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { setSelected(new Set()); }, [filters]);
  const load = useCallback(async () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    setData((await api.get("/bills", { params: p })).data);
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const open = (b) => setParams({ bill: b.id });
  const ids = data?.items.map((b) => b.id) || [];
  const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/bills/bulk-delete", { ids: [...selected] });
      toast.success(`${r.deleted} ${r.deleted === 1 ? "bill" : "bills"} deleted`);
      setBulkOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBulkBusy(false);
  };
  const dateInput = (k, label) => (
    <div className="min-w-0"><label className="eyebrow">{label}</label>
      <Input type="date" value={filters[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5 h-10 rounded-xl" data-testid={`bills-filter-${k.replace("_", "-")}`} /></div>
  );

  return (
    <div>
      <PageHeader eyebrow="Utilities & supplier bills" title="Bills" subtitle="Only payments count as expenses. Unpaid amounts never do." testId="bills-title">
        <Button onClick={() => { setPrefill(null); setAdding(true); }} className="rounded-xl h-10" data-testid="add-bill-button"><Plus className="h-4 w-4 mr-1" />Add bill</Button>
      </PageHeader>
      <div className="mb-4"><ScanButton purpose="bill" onResult={onScan} testPrefix="bills-scan" /></div>
      <div className="surface p-4 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 fade-up" data-testid="bills-filters">
        <div className="min-w-0"><label className="eyebrow">Status</label>
          <Select value={filters.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="bills-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "Unpaid", "Partly paid", "Overdue", "Paid"].map((s) => <SelectItem key={s} value={s} data-testid={`bills-status-option-${s.replace(" ", "-").toLowerCase()}`}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="min-w-0"><label className="eyebrow">Category</label>
          <Select value={filters.category_id} onValueChange={(v) => set("category_id", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="bills-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`bills-category-option-${c.key}`}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="min-w-0"><label className="eyebrow">Vendor</label>
          <Input value={filters.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="e.g. BPL" className="mt-1.5 h-10 rounded-xl" data-testid="bills-filter-vendor" /></div>
        {dateInput("due_from", "Due from")}{dateInput("due_to", "Due to")}{dateInput("period_from", "Period from")}{dateInput("period_to", "Period to")}
      </div>
      {data && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl bg-foreground text-white px-5 py-3.5 my-5" data-testid="bills-totals">
          <span className="text-xs font-semibold uppercase tracking-widest text-white/70">{data.count} {data.count === 1 ? "bill" : "bills"}</span>
          <span className="text-sm">Owed <b className="font-num" data-testid="bills-total-owed">{money(data.totals.owed_cents)}</b></span>
          <span className="text-sm">Paid <b className="font-num text-emerald-300" data-testid="bills-total-paid">{money(data.totals.paid_cents)}</b></span>
          <span className="text-sm">Balance <b className="font-num text-amber-300" data-testid="bills-total-balance">{money(data.totals.balance_cents)}</b></span>
          {JSON.stringify(filters) !== JSON.stringify(EMPTY) && <button onClick={() => setFilters(EMPTY)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white/80" data-testid="bills-clear-filters"><X className="h-3.5 w-3.5" />Clear filters</button>}
        </div>
      )}
      <BulkBar count={selected.size} total={data?.count} testPrefix="bills" canSelectAll={!allSel}
        onSelectAll={() => setSelected(new Set(ids))} onClear={() => setSelected(new Set())} onDelete={() => setBulkOpen(true)} />
      <BulkDeleteDialog open={bulkOpen} busy={bulkBusy} testPrefix="bills" title={`Delete ${selected.size} bills and their payments? This can't be undone.`}
        onCancel={() => setBulkOpen(false)} onConfirm={bulkDelete} />
      <div className="surface divide-y divide-border overflow-hidden" data-testid="bills-list">
        {data?.items.length > 0 && (
          <label className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground bg-secondary/40">
            <Checkbox checked={allSel} onCheckedChange={() => setSelected(allSel ? new Set() : new Set(ids))} data-testid="bills-select-all" />Select all
          </label>
        )}
        {data?.items.length === 0 && <EmptyState icon={ReceiptText} text="No bills found" testId="bills-empty" />}
        {data?.items.map((b) => (
          <div key={b.id} className={`flex items-stretch ${selected.has(b.id) ? "bg-rose-50/60" : ""}`}>
            <div className="pl-4 flex items-center"><Checkbox checked={selected.has(b.id)} onCheckedChange={() => toggle(b.id)} className="h-5 w-5" aria-label="Select bill" data-testid={`bill-select-${b.id}`} /></div>
            <div className="flex-1 min-w-0"><BillSummaryRow b={b} onOpen={open} /></div>
          </div>
        ))}
      </div>
      <BillFormDialog open={adding} prefill={prefill?.form} ai={prefill?.ai} onClose={() => { setAdding(false); setPrefill(null); }} onSaved={() => { setAdding(false); toast.success("Bill added"); load(); }} />
      <BillDetailDialog billId={openId} onClose={() => setParams({})} onChanged={load} />
    </div>
  );
}
